import { Box, Container, Typography, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const CustomButton = ({ variant, id, children }) => {
  const theme = useTheme();
  const isContained = variant === 'contained';

  const handleClick = () => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 70; // Navbar height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  
  return (
    <Box
      component="button"
      onClick={handleClick}
      sx={{
        display: 'inline-block',
        py: { xs: 1.5, md: 2 },
        px: { xs: 3, md: 4 },
        fontSize: { xs: '0.9rem', md: '1rem' },
        fontWeight: 600,
        borderRadius: 2,
        cursor: 'pointer',
        backgroundColor: isContained ? theme.palette.primary.main : 'transparent',
        color: isContained ? '#fff' : '#fff',
        border: isContained ? 'none' : '2px solid rgba(255, 255, 255, 0.5)',
        textDecoration: 'none',
        transition: 'all 0.3s ease',
        textAlign: 'center',
        '&:hover': {
          backgroundColor: isContained ? theme.palette.primary.dark : 'rgba(255, 255, 255, 0.1)',
          border: isContained ? 'none' : '2px solid rgba(255, 255, 255, 0.8)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      {children}
    </Box>
  );
};

const Hero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  return (
    <Box
      id="home"
      component="section"
      sx={{
        minHeight: 'calc(100vh - var(--navbar-height))',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#0a0a0a',
        position: 'relative',
        overflow: 'hidden',
        mt: 'var(--navbar-height)',
        scrollMarginTop: 'var(--navbar-height)',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url("/Nuyu-media/Nuyu Pattern_1.png")',
          backgroundRepeat: 'repeat',
          opacity: 0.03,
          zIndex: 1,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 6, md: 8 },
            textAlign: { xs: 'left', md: 'left' },
          }}
        >
          {/* Text Content */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            sx={{ 
              flex: 1,
              maxWidth: { xs: '100%', md: '50%' }
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Typography
                variant="h1"
                sx={{
                  color: 'common.white',
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                  lineHeight: { xs: 1.2, md: 1.1 },
                  mb: { xs: 2, md: 3 },
                  letterSpacing: '-0.02em',
                }}
              >
                Transform Your Digital Presence
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  mb: { xs: 4, md: 5 },
                  fontWeight: 400,
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  lineHeight: 1.6,
                  maxWidth: '600px',
                }}
              >
                We create stunning digital experiences that captivate your audience and drive results.
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: { xs: 2, md: 3 },
                  flexDirection: { xs: 'column', sm: 'row' },
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                <CustomButton variant="contained" id="services">
                  Our Services
                </CustomButton>
                <CustomButton variant="outlined" id="contact">
                  Contact Us
                </CustomButton>
              </Box>
            </motion.div>
          </Box>

          {/* Image/Illustration */}
          {!isMobile && (
            <Box
              component={motion.div}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                maxWidth: { xs: '100%', md: '50%' },
              }}
            >
              <Box
                component="img"
                src="https://i.imgur.com/PUq9yby.png"
                alt="Digital Transformation"
                sx={{
                  width: '100%',
                  maxWidth: { sm: '400px', md: '500px' },
                  height: 'auto',
                  filter: 'brightness(0) invert(1)',
                  opacity: 0.9,
                }}
              />
            </Box>
          )}
        </Box>

        {/* Scroll Indicator */}
        {!isTablet && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              y: [0, 10, 0]
            }}
            transition={{
              opacity: { delay: 1, duration: 0.5 },
              y: {
                duration: 1.5,
                repeat: Infinity,
                repeatType: 'loop',
                ease: 'easeInOut'
              }
            }}
            style={{
              position: 'absolute',
              bottom: 40,
              left: '50%',
              transform: 'translateX(-50%)',
              cursor: 'pointer',
              textAlign: 'center',
              willChange: 'transform',
              zIndex: 10
            }}
            onClick={() => {
              const nextSection = document.getElementById('services');
              if (nextSection) {
                const offset = 70; // Navbar height
                const elementPosition = nextSection.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - offset;

                window.scrollTo({
                  top: offsetPosition,
                  behavior: 'smooth'
                });
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                mb: 1,
                fontSize: '0.9rem',
              }}
            >
              Scroll to explore
            </Typography>
            <KeyboardArrowDownIcon 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '2rem',
              }} 
            />
          </motion.div>
        )}
      </Container>
    </Box>
  );
};

export default Hero;
